/*
 * newsbot - forked from autobrr
 */

import { useState } from "react";
import {
  SparklesIcon,
  CheckIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";

import { toast } from "@components/hot-toast";
import Toast from "@components/notifications/Toast";
import { EmptySimple } from "@components/emptystates";
import { RingResizeSpinner } from "@components/Icons";
import {
  useCachedSuggestions,
  useGenerateSuggestions,
  useApplyKeywordSuggestion,
  useApplyNewCategory,
} from "@api/queries";
import type { KeywordSuggestion, NewCategorySuggestion } from "@/types";

function formatTimeAgo(isoString: string): string {
  const date = new Date(isoString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);

  if (diffMins < 1) return "just now";
  if (diffMins < 60) return `${diffMins} minute${diffMins === 1 ? "" : "s"} ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? "" : "s"} ago`;

  const diffDays = Math.floor(diffHours / 24);
  return `${diffDays} day${diffDays === 1 ? "" : "s"} ago`;
}

export function Suggestions() {
  const [dismissedKeywords, setDismissedKeywords] = useState<Set<string>>(new Set());
  const [dismissedNewCategories, setDismissedNewCategories] = useState<Set<string>>(new Set());

  const { data: cachedData, isLoading: isLoadingCache } = useCachedSuggestions();
  const suggestions = cachedData?.suggestions || null;
  const cacheInfo = cachedData?.cache_info;

  const generateSuggestions = useGenerateSuggestions();
  const applyKeywordSuggestion = useApplyKeywordSuggestion();
  const applyNewCategory = useApplyNewCategory();

  const handleGenerate = () => {
    // Clear dismissed state when regenerating
    setDismissedKeywords(new Set());
    setDismissedNewCategories(new Set());

    generateSuggestions.mutate(
      { story_limit: 100, force_refresh: true },
      {
        onSuccess: (data) => {
          toast.custom((t) => (
            <Toast
              type="success"
              body={`Analyzed ${data.stories_analyzed} stories and found ${data.keyword_suggestions.length + data.new_category_suggestions.length} suggestions`}
              t={t}
            />
          ));
        },
        onError: (error) => {
          toast.custom((t) => (
            <Toast type="error" body={`Failed to generate suggestions: ${String(error)}`} t={t} />
          ));
        },
      }
    );
  };

  const handleApplyKeywordSuggestion = (suggestion: KeywordSuggestion) => {
    applyKeywordSuggestion.mutate(
      {
        category_id: suggestion.category_id,
        keywords: suggestion.keywords_to_add,
      },
      {
        onSuccess: () => {
          setDismissedKeywords((prev) => new Set(prev).add(suggestion.category_id));
          toast.custom((t) => (
            <Toast
              type="success"
              body={`Added ${suggestion.keywords_to_add.length} keywords to ${suggestion.category_name}`}
              t={t}
            />
          ));
        },
        onError: (error) => {
          toast.custom((t) => (
            <Toast type="error" body={`Failed to apply suggestion: ${String(error)}`} t={t} />
          ));
        },
      }
    );
  };

  const handleApplyNewCategory = (suggestion: NewCategorySuggestion) => {
    applyNewCategory.mutate(
      {
        name: suggestion.name,
        description: suggestion.description,
        keywords: suggestion.keywords,
      },
      {
        onSuccess: () => {
          setDismissedNewCategories((prev) => new Set(prev).add(suggestion.name));
          toast.custom((t) => (
            <Toast type="success" body={`Created new category: ${suggestion.name}`} t={t} />
          ));
        },
        onError: (error) => {
          toast.custom((t) => (
            <Toast type="error" body={`Failed to create category: ${String(error)}`} t={t} />
          ));
        },
      }
    );
  };

  const handleDismissKeywordSuggestion = (categoryId: string) => {
    setDismissedKeywords((prev) => new Set(prev).add(categoryId));
  };

  const handleDismissNewCategory = (name: string) => {
    setDismissedNewCategories((prev) => new Set(prev).add(name));
  };

  const visibleKeywordSuggestions =
    suggestions?.keyword_suggestions.filter((s) => !dismissedKeywords.has(s.category_id)) || [];
  const visibleNewCategorySuggestions =
    suggestions?.new_category_suggestions.filter((s) => !dismissedNewCategories.has(s.name)) || [];

  const hasVisibleSuggestions =
    visibleKeywordSuggestions.length > 0 || visibleNewCategorySuggestions.length > 0;

  return (
    <main>
      <div className="my-6 max-w-(--breakpoint-xl) mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-black dark:text-white">Smart Category Suggestions</h1>
            {cacheInfo && (
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
                Last updated: {formatTimeAgo(cacheInfo.created_at)}
              </p>
            )}
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={generateSuggestions.isPending}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {generateSuggestions.isPending ? (
              <>
                <RingResizeSpinner className="w-4 h-4" />
                Analyzing Stories...
              </>
            ) : (
              <>
                <SparklesIcon className="w-4 h-4" />
                {suggestions ? "Refresh Suggestions" : "Generate Suggestions"}
              </>
            )}
          </button>
        </div>
      </div>

      <div className="max-w-(--breakpoint-xl) mx-auto pb-6 px-2 sm:px-6 lg:pb-16 lg:px-8">
        {isLoadingCache ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md p-8">
            <div className="flex flex-col items-center gap-4">
              <RingResizeSpinner className="w-8 h-8 text-blue-600" />
              <p className="text-gray-600 dark:text-gray-400">Loading suggestions...</p>
            </div>
          </div>
        ) : generateSuggestions.isPending ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md p-8">
            <div className="flex flex-col items-center gap-4">
              <RingResizeSpinner className="w-8 h-8 text-blue-600" />
              <p className="text-gray-600 dark:text-gray-400">
                Analyzing stories and generating smart suggestions...
              </p>
            </div>
          </div>
        ) : !suggestions ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md">
            <EmptySimple
              title="No suggestions yet"
              subtitle="Click 'Generate Suggestions' to analyze your stories and get smart category recommendations"
            />
          </div>
        ) : !hasVisibleSuggestions ? (
          <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md">
            <EmptySimple
              title="No suggestions found"
              subtitle={`Analyzed ${suggestions.stories_analyzed} stories. Your categories are well-tuned!`}
            />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Keyword Suggestions Section */}
            {visibleKeywordSuggestions.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  Keyword Additions ({visibleKeywordSuggestions.length})
                </h2>
                <div className="space-y-3">
                  {visibleKeywordSuggestions.map((suggestion) => (
                    <KeywordSuggestionCard
                      key={suggestion.category_id}
                      suggestion={suggestion}
                      onAccept={() => handleApplyKeywordSuggestion(suggestion)}
                      onDismiss={() => handleDismissKeywordSuggestion(suggestion.category_id)}
                      isApplying={applyKeywordSuggestion.isPending}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* New Category Suggestions Section */}
            {visibleNewCategorySuggestions.length > 0 && (
              <div>
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-3">
                  New Categories ({visibleNewCategorySuggestions.length})
                </h2>
                <div className="space-y-3">
                  {visibleNewCategorySuggestions.map((suggestion) => (
                    <NewCategorySuggestionCard
                      key={suggestion.name}
                      suggestion={suggestion}
                      onAccept={() => handleApplyNewCategory(suggestion)}
                      onDismiss={() => handleDismissNewCategory(suggestion.name)}
                      isApplying={applyNewCategory.isPending}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </main>
  );
}

interface KeywordSuggestionCardProps {
  suggestion: KeywordSuggestion;
  onAccept: () => void;
  onDismiss: () => void;
  isApplying: boolean;
}

function KeywordSuggestionCard({
  suggestion,
  onAccept,
  onDismiss,
  isApplying,
}: KeywordSuggestionCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {suggestion.category_name}
            </h3>
            <span className="px-2 py-0.5 text-xs rounded bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300">
              Keyword Addition
            </span>
          </div>

          <div className="mb-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-medium">Suggested keywords:</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestion.keywords_to_add.map((keyword) => (
                <span
                  key={keyword}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-blue-50 text-blue-700 dark:bg-blue-900/20 dark:text-blue-300 border border-blue-200 dark:border-blue-800"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Reasoning:</span> {suggestion.reasoning}
            </p>
          </div>

          {suggestion.sample_stories.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 font-medium">
                Sample stories:
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
                {suggestion.sample_stories.slice(0, 3).map((story) => (
                  <li key={story.id} className="truncate">
                    {story.headline}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={onAccept}
            disabled={isApplying}
            className="p-2 rounded-md bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Accept suggestion"
          >
            <CheckIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={isApplying}
            className="p-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Dismiss suggestion"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}

interface NewCategorySuggestionCardProps {
  suggestion: NewCategorySuggestion;
  onAccept: () => void;
  onDismiss: () => void;
  isApplying: boolean;
}

function NewCategorySuggestionCard({
  suggestion,
  onAccept,
  onDismiss,
  isApplying,
}: NewCategorySuggestionCardProps) {
  return (
    <div className="bg-white dark:bg-gray-800 border border-gray-250 dark:border-gray-775 shadow-table rounded-md p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              {suggestion.name}
            </h3>
            <span className="px-2 py-0.5 text-xs rounded bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300">
              New Category
            </span>
          </div>

          <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
            {suggestion.description}
          </p>

          <div className="mb-3">
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-2">
              <span className="font-medium">Keywords:</span>
            </p>
            <div className="flex flex-wrap gap-2">
              {suggestion.keywords.map((keyword) => (
                <span
                  key={keyword}
                  className="px-2 py-1 text-xs font-medium rounded-md bg-purple-50 text-purple-700 dark:bg-purple-900/20 dark:text-purple-300 border border-purple-200 dark:border-purple-800"
                >
                  {keyword}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              <span className="font-medium">Reasoning:</span> {suggestion.reasoning}
            </p>
          </div>

          {suggestion.sample_stories.length > 0 && (
            <div>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-1 font-medium">
                Sample stories:
              </p>
              <ul className="text-sm text-gray-600 dark:text-gray-400 list-disc list-inside space-y-1">
                {suggestion.sample_stories.slice(0, 3).map((story) => (
                  <li key={story.id} className="truncate">
                    {story.headline}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            onClick={onAccept}
            disabled={isApplying}
            className="p-2 rounded-md bg-green-600 text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Accept suggestion"
          >
            <CheckIcon className="w-5 h-5" />
          </button>
          <button
            type="button"
            onClick={onDismiss}
            disabled={isApplying}
            className="p-2 rounded-md bg-gray-200 text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 disabled:opacity-50 disabled:cursor-not-allowed"
            title="Dismiss suggestion"
          >
            <XMarkIcon className="w-5 h-5" />
          </button>
        </div>
      </div>
    </div>
  );
}
